import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from '@/components/common/breadcrumbs'
import { Separator } from '@/components/common/separator'
import { type PresetKey, VaultPresetSelector } from '@/components/shared/vaultPresets'
import { VaultsFiltersModal } from '@/components/shared/vaultsFilters'
import { routes } from '@/config/routes'
import { useAccountContext } from '@/context/account'
import { useFilterPresets } from '@/hooks/useFilterPresets'
import { useFilteredVaults } from '@/hooks/useFilteredVaults'
import { useVaultsSearchState } from '@/hooks/useVaultsSearchState'
import { useVaultsWithPositions } from '@/hooks/useVaultsWithPositions'
import { usePositionsQuery } from '@/queries/positions.query'
import { type VaultsQueryInitialData, useVaultsQuery } from '@/queries/vaults.query'
import { useMemo } from 'react'
import { VaultsCards } from './vaultsCards'
import { VaultsTable } from './vaultsTable'

const PRESETS: PresetKey[] = ['all', 'tvl', 'eth', 'usd']

interface VaultsDiscoverProps {
  initialData: VaultsQueryInitialData
}

export const VaultsDiscover = ({ initialData }: VaultsDiscoverProps) => {
  const { account } = useAccountContext()
  const { data: vaults } = useVaultsQuery({ initialData })
  const {
    apyRange,
    searchFor,
    selectedNetworks,
    selectedTokens,
    selectedProtocols,
    selectedCurators,
    selectedTags,
    selectedTvlRange,
    onlyTransactional,
    minVaultScore,
  } = useVaultsSearchState()
  const { data: positionsList } = usePositionsQuery({
    address: account,
    apyInterval: apyRange,
  })
  const vaultsWithPositions = useMemo(
    () => useVaultsWithPositions(vaults ?? [], positionsList),
    [vaults, positionsList]
  )
  const { activePreset, handlePresetClick } = useFilterPresets({ vaults: vaultsWithPositions })
  const filteredVaults = useFilteredVaults({
    vaults: vaultsWithPositions,
    searchFor,
    selectedNetworks,
    selectedTokens,
    selectedProtocols,
    selectedCurators,
    selectedTags,
    selectedTvlRange,
    onlyTransactional,
    minVaultScore,
  })

  return (
    <>
      <div className="flex h-12 w-full border-b bg-page md:hidden">
        <Breadcrumb className="z-0 h-full w-full border-none bg-page">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink to={routes.root} title="Discover opportunities">
                Discover
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <VaultPresetSelector presets={PRESETS} activePreset={activePreset} onPresetClick={handlePresetClick} />
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <Separator orientation="vertical" className="h-full" />
        <VaultsFiltersModal
          vaults={vaultsWithPositions}
          numberOfVaults={filteredVaults.length}
          className="h-full w-12 min-w-12"
          trackingParams={{ Page: 'Discover' }}
        />
      </div>
      <VaultsTable
        vaults={vaultsWithPositions}
        presets={PRESETS}
        activePreset={activePreset}
        onPresetClick={handlePresetClick}
        className="hidden md:flex"
      />
      <VaultsCards vaults={vaultsWithPositions} className="md:hidden" />
    </>
  )
}
